/**
 * Phase 8 · Cluster C-43 — Internal sourcing connector.
 *
 * Spec:  docs/ux/PHASE8_WRANGLE_TRANSFORMATION.md Addendum F §33.7
 * Smoke: docs/ux/PHASE8_VISUAL_TRACKER.md §8.7
 *
 * Queries the org's own `ggj_b2b_candidates` pool with a narrowed
 * SourcingQuery. This connector is always "active" — no credential row
 * is required in `ggj_b2b_source_connectors`, which means an empty org
 * with zero integrations still gets useful agent output on day one.
 *
 * Read-only. Uses the frontend Supabase client so row-level security
 * enforces the org scope; we still include `eq('org_id', orgId)` for
 * belt-and-braces and to hit the existing (org_id, ...) indexes.
 */
import { supabase } from '@core/supabase/client';
import {
  clampLimit,
  type ConnectorHealth,
  type SourcingConnector,
  type SourcingQuery,
  type SourcingResult,
} from './types';

interface CandidateRow {
  id: string;
  full_name: string | null;
  current_title: string | null;
  current_company: string | null;
  location: string | null;
  linkedin_url: string | null;
  avatar_url: string | null;
  parsed_skills: string[] | null;
  years_exp: number | null;
}

// Columns we fetch to build the SourcingResult. Kept tight because the
// pool can be thousands of rows and every extra column costs JSON weight.
const CANDIDATE_FIELDS =
  'id, full_name, current_title, current_company, location, linkedin_url, avatar_url, parsed_skills, years_exp';

/**
 * Decide which candidate fields are worth OR-matching with ILIKE on the
 * server. We keep the list short (name / title / company) so PostgREST
 * doesn't time out on a large pool; skills are matched client-side after
 * the first page because the `parsed_skills` text[] overlap operator is
 * not exposed by the REST client in a portable way.
 */
function buildServerFilter(keywords: string[]): string | null {
  const tokens = keywords
    .map((k) => k.trim())
    .filter((k) => k.length >= 2)
    .slice(0, 4); // ≤ 4 OR-clauses keeps the query plan trivial
  if (tokens.length === 0) return null;
  const fields = ['full_name', 'current_title', 'current_company'] as const;
  const clauses: string[] = [];
  for (const tok of tokens) {
    const escaped = tok.replace(/[%_]/g, ''); // strip PostgREST LIKE metas
    for (const f of fields) clauses.push(`${f}.ilike.%${escaped}%`);
  }
  return clauses.join(',');
}

function toResult(row: CandidateRow): SourcingResult {
  const skillsArr = Array.isArray(row.parsed_skills) ? row.parsed_skills : [];
  return {
    externalId: row.id, // internal rows are already our canonical id
    name: row.full_name ?? '(unnamed)',
    headline: [row.current_title, row.current_company]
      .filter((s): s is string => Boolean(s && s.trim()))
      .join(' @ ') || undefined,
    locationText: row.location ?? undefined,
    profileUrl: row.linkedin_url ?? `/candidates/${row.id}`,
    avatarUrl: row.avatar_url ?? undefined,
    signals: {
      skills: skillsArr,
      yearsExp: row.years_exp ?? null,
    },
    source: 'internal',
  };
}

export const internalConnector: SourcingConnector = {
  kind: 'internal',

  async search(query: SourcingQuery, orgId: string): Promise<SourcingResult[]> {
    if (!orgId) return [];
    const limit = clampLimit(query.limit);

    // Pull a slightly wider page from the DB so client-side refinement
    // (skills overlap, experience range) still returns a full `limit`
    // after filtering. 2x is a safe multiplier against typical skill
    // coverage; we cap it so the over-fetch never exceeds 200.
    const overFetch = Math.min(limit * 2, 200);

    let request = supabase
      .from('ggj_b2b_candidates')
      .select(CANDIDATE_FIELDS)
      .eq('org_id', orgId)
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .limit(overFetch);

    const filter = buildServerFilter(query.keywords ?? []);
    if (filter) request = request.or(filter);
    if (query.location) {
      request = request.ilike('location', `%${query.location.replace(/[%_]/g, '')}%`);
    }
    if (typeof query.experienceYearsMin === 'number') {
      request = request.gte('years_exp', query.experienceYearsMin);
    }
    if (typeof query.experienceYearsMax === 'number') {
      request = request.lte('years_exp', query.experienceYearsMax);
    }

    const { data, error } = await request;
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as CandidateRow[];

    const wantedSkills = (query.skills ?? [])
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    const filtered = wantedSkills.length
      ? rows.filter((r) => {
          const have = (r.parsed_skills ?? []).map((s) => s.toLowerCase());
          return wantedSkills.some((w) => have.some((h) => h.includes(w)));
        })
      : rows;

    return filtered.slice(0, limit).map(toResult);
  },

  async healthcheck(_orgId: string): Promise<ConnectorHealth> {
    // No external dependency — the only way internal fails is if Supabase
    // itself is unreachable, which would already have broken the rest of
    // the app. Report healthy.
    return { ok: true };
  },
};
